import React, { type FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import notification from "@app/services/notification";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import {
    BusinessDivisionValue,
    BusinessMeasureValue,
    BusinessStockObjValue,
    BusinessUserValue,
} from "@src/components/business-data-display";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import { OverflowMarquee } from "@src/shared/ui";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { formatEmptyDisplayValue } from "@shared/utils/formatEmptyDisplayValue";
import {
    StockKanbanComplectState,
    stockKanbanContainersServices,
    type StockKanbanContainer,
    type StockKanbanContainerExtends,
    type StockKanbanContainerFilter,
} from "../../../../api/stockKanbanContainers";
import { formatComplectState } from "../../helpers/formatComplectState";
import { formatRemainderCells } from "../../helpers/formatRemainderCells";
import { stockKanbanSupplyListWindows } from "./stockKanbanSupplyList.windows";
import { useStockKanbanSupplyListMutations } from "./useStockKanbanSupplyListMutations";
import "./StockKanbanSupplyList.scss";

const CONTAINER_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-kanban-supply.list.scan-container",
    title: "Сканируйте этикетку контейнера КАНБАН",
    description: "Добавим контейнер в выборку и при необходимости возьмём на комплектование.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

const STOCK_KANBAN_SUPPLY_LIST_SCAN_PAUSE_WINDOW_KEYS = [stockKanbanSupplyListWindows.filter.key];

/** Список контейнеров КАНБАН для обеспечения производства. */
export const StockKanbanSupplyList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const actionSheet = useActionSheet();
    const filterState = useUrlFilter<StockKanbanContainerFilter>();
    const isListScannerSuppressedByWindow = useIsOverlayOpenByKeys(STOCK_KANBAN_SUPPLY_LIST_SCAN_PAUSE_WINDOW_KEYS);

    const list = useBusinessListQuery<StockKanbanContainer, StockKanbanContainerFilter>({
        queryKey: "stock.kanbanSupply.containers",
        filter: filterState.filter,
        queryFn: async (filter, signal) => {
            const result = await stockKanbanContainersServices.load(
                {
                    filter,
                    include: [
                        "StockObj",
                        "Measure",
                        "Division",
                        "Section",
                        "ComplectUser",
                        "RemainderCells",
                    ] satisfies Array<keyof StockKanbanContainerExtends>,
                },
                { signal },
            );
            return result;
        },
        getId: (item) => item.SoCode,
    });

    const finishedContainerCodes = useMemo(
        () =>
            list.data.filter((item) => item.ComplectState === StockKanbanComplectState.Finished).map((item) => item.SoCode),
        [list.data],
    );

    const mutations = useStockKanbanSupplyListMutations({
        onContainerCreate: (container) => {
            const exists = list.data.some((item) => item.SoCode === container.SoCode);
            if (exists) {
                notification.warning("Сканированный контейнер уже есть в списке.");
                void list.refetch();
                return;
            }
            list.mutate.createItems([container]);
        },
        onSelectionMove: () => list.refetch(),
    });

    const openFilterWindow = (): void => {
        stockKanbanSupplyListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (filter) => {
                filterState.applyFilter(filter, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            if (filterState.filter == null) {
                notification.warning("Перед сканированием контейнера выберите склад в фильтре.");
                openFilterWindow();
                return;
            }

            await mutations.createByScanMutation.mutateAsync({
                barcode,
                filter: filterState.filter,
            });
        },
        [filterState.filter, mutations.createByScanMutation],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled: filterState.filter != null && !isListScannerSuppressedByWindow,
            priority: "screen",
            captureInEditable: false,
            hint: CONTAINER_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.kanbanSupply.list.scanContainer",
        }),
        [filterState.filter, handleScan, isListScannerSuppressedByWindow],
    );

    useScannerRegistration(scannerRegistration);

    const actionSheetSections = useMemo<ActionSheetSection[]>(
        () => [
            {
                id: "kanban-supply",
                items: [
                    {
                        type: "action",
                        id: "move-completed-selection",
                        label: "Перемещение всех скомплектованных контейнеров в выборке",
                        description:
                            finishedContainerCodes.length === 0
                                ? "В выборке нет контейнеров со статусом «комплектование завершено»"
                                : `Будет перемещено контейнеров: ${finishedContainerCodes.length}`,
                        disabled: finishedContainerCodes.length === 0,
                        loading: mutations.moveCompletedSelectionMutation.isPending,
                        shouldCloseOnSelect: false,
                        onSelect: (sheet) => {
                            if (finishedContainerCodes.length === 0) {
                                return;
                            }
                            sheet.close();
                            void mutations.moveCompletedSelectionMutation.mutateAsync({
                                codes: finishedContainerCodes,
                            });
                        },
                    },
                ],
            },
        ],
        [finishedContainerCodes, mutations.moveCompletedSelectionMutation],
    );

    return (
        <div className="stock_kanban_supply_list_page">
            <CardHeader
                text="Комплектация «КАНБАН»"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => {
                    return (
                        <ListItem
                            key={item.SoCode}
                            tone={item.ComplectState === StockKanbanComplectState.Finished ? "success" : "default"}
                            title={`Контейнер №${item.Num}`}
                            props={[
                                {
                                    label: "Номенклатура",
                                    value: <BusinessStockObjValue stockObj={item.StockObj} mode="wrap" />,
                                },
                                {
                                    label: "Норм. кол-во",
                                    value: <BusinessMeasureValue value={`${item.Qty}`} measure={item.Measure} />,
                                },
                                {
                                    label: "Статус комплект.",
                                    value: formatComplectState(item.ComplectState),
                                },
                                {
                                    label: "Дата взятия на комплект.",
                                    value: formatEmptyDisplayValue(item.ComplectDate),
                                },
                                {
                                    label: "Потребитель",
                                    value: <BusinessDivisionValue division={item.Division} section={item.Section} />,
                                },
                                {
                                    label: "Ячейки остатка",
                                    value: <OverflowMarquee>{formatRemainderCells(item.RemainderCells)}</OverflowMarquee>,
                                },
                                { label: "Комплектовщик", value: <BusinessUserValue user={item.ComplectUser} /> },
                            ]}
                            onClick={() => {
                                navigate({ pathname: `${item.SoCode}` });
                            }}
                        />
                    );
                })}
            </BusinessList>
        </div>
    );
};
